import React, { useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { KeyRound, Lock, ShieldCheck, TriangleAlert, Loader2, X } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import { useThemeColor } from '../hooks/useThemeColor';
import { useToast } from '../components/Toast';
import { fetchUserData } from '../slice/userDataSlice';
import { BASE_AUTH_URL } from '../components/constants';
import { authHeader } from '../utils/authHeaders';
import MfaEnrollment, { DigitCodeInput, RecoveryCodesPanel } from '../components/MfaEnrollment';

const outlineButtonClass = 'px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';
const primaryButtonClass = 'px-4 py-2 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';

const AccountModal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[120] p-4">
    <div className="bg-white rounded-2xl w-full max-w-[480px] shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
      <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB]">
        <h2 className="text-[20px] font-bold text-[#111827]">{title}</h2>
        <button type="button" onClick={onClose} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors">
          <X size={20} className="text-[#6B7280]" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
    </div>
  </div>
);

const Account = () => {
  const { t } = useTranslation();
  const { primaryColor } = useThemeColor();
  const dispatch = useDispatch();
  const { addToast, ToastContainer } = useToast();
  const { data } = useSelector((state) => state.admin);

  // null | 'setup' | 'regenerate' | 'codes'
  const [modal, setModal] = useState(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const totpEnabled = !!data?.totp_enabled;

  const refreshUserData = () => {
    const email = localStorage.getItem('email');
    if (email) dispatch(fetchUserData(email));
  };

  const openModal = (next) => {
    setError('');
    setCode('');
    setModal(next);
  };

  const closeModal = () => {
    if (loading) return;
    setModal(null);
    setCode('');
    setError('');
    setRecoveryCodes([]);
  };

  const finishSetup = () => {
    setModal(null);
    addToast(t('auth.mfaEnabledSuccess'), 'success');
    refreshUserData();
  };

  const confirmRegenerate = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${BASE_AUTH_URL}/mfa/recovery-codes`, { code }, { headers: authHeader() });
      setCode('');
      setRecoveryCodes(res.data.recovery_codes || []);
      setModal('codes');
    } catch (err) {
      setError(err?.response?.data?.detail || t('auth.genericError'));
    } finally {
      setLoading(false);
    }
  };

  const finishCodes = () => {
    setModal(null);
    setRecoveryCodes([]);
    addToast(t('auth.mfaRegenerateSuccess'), 'success');
  };

  return (
    <div className="h-screen bg-white overflow-y-auto">
      <ToastContainer />
      <header className="px-10 h-[80px] flex items-center flex-shrink-0">
        <h1 className="text-[22px] font-bold text-[#111827]">{t('nav.account')}</h1>
      </header>

      <div className="px-10 py-8">
        <div className="mb-6">
          <h2 className="text-[18px] font-semibold text-[#111827]">{t('auth.mfaSectionTitle')}</h2>
          <p className="text-[14px] text-[#6B7280] mt-1">{t('auth.mfaSectionDescription')}</p>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB]">
          <div className="flex items-center gap-4 px-6 py-5">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${primaryColor}14`, color: primaryColor }}
            >
              <ShieldCheck size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-medium text-[#111827]">{t('auth.mfaAuthenticatorRowTitle')}</p>
              <p className="text-[13px] text-[#6B7280] mt-0.5 flex items-center gap-1.5">
                <Lock size={13} className="flex-shrink-0" />
                {t('auth.mfaRequiredForAll')}
              </p>
            </div>
            {totpEnabled ? (
              <span className="px-3 py-1 text-[12px] font-medium text-[#059669] bg-[#ECFDF5] border border-[#A7F3D0] rounded-full flex-shrink-0">
                {t('auth.mfaStatusEnabled')}
              </span>
            ) : (
              <button type="button" onClick={() => openModal('setup')} className={primaryButtonClass} style={{ backgroundColor: primaryColor }}>
                {t('auth.mfaEnableButton')}
              </button>
            )}
          </div>

          {totpEnabled && (
            <div className="flex items-center gap-4 px-6 py-5">
              <div className="w-10 h-10 rounded-full bg-[#F3F4F6] text-[#6B7280] flex items-center justify-center flex-shrink-0">
                <KeyRound size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-medium text-[#111827]">{t('auth.mfaRecoverySectionTitle')}</p>
                <p className="text-[13px] text-[#6B7280] mt-0.5">{t('auth.mfaRecoverySectionText')}</p>
              </div>
              <button type="button" onClick={() => openModal('regenerate')} className={outlineButtonClass}>
                {t('auth.mfaRegenerateButton')}
              </button>
            </div>
          )}
        </div>
      </div>

      {modal === 'setup' && (
        <AccountModal title={t('auth.mfaSetupTitle')} onClose={closeModal}>
          <MfaEnrollment
            authorization={authHeader().Authorization}
            primaryColor={primaryColor}
            onComplete={finishSetup}
            onCancel={closeModal}
          />
        </AccountModal>
      )}

      {modal === 'regenerate' && (
        <AccountModal title={t('auth.mfaRegenerateConfirmTitle')} onClose={closeModal}>
          <form onSubmit={confirmRegenerate}>
            <p className="text-[14px] text-[#6B7280]">{t('auth.mfaRegenerateSubtitle')}</p>

            <div className="flex items-start gap-2.5 mt-4 text-[13px] text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-4 py-3">
              <TriangleAlert size={16} className="flex-shrink-0 mt-0.5 text-[#D97706]" />
              <span>{t('auth.mfaRegenerateWarning')}</span>
            </div>

            <div className="mt-6">
              <DigitCodeInput value={code} onChange={setCode} autoFocus primaryColor={primaryColor} />
            </div>

            {error && (
              <p className="mt-3 text-[13px] text-[#DC2626] text-center">{error}</p>
            )}

            <div className="flex items-center justify-end gap-3 -mx-6 -mb-5 mt-6 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB]">
              <button
                type="button"
                onClick={closeModal}
                disabled={loading}
                className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="px-6 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors min-w-[100px] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : t('auth.mfaRegenerateSubmit')}
              </button>
            </div>
          </form>
        </AccountModal>
      )}

      {modal === 'codes' && (
        <AccountModal title={t('auth.mfaRecoveryTitle')} onClose={finishCodes}>
          <RecoveryCodesPanel codes={recoveryCodes} primaryColor={primaryColor} onDone={finishCodes} compact />
        </AccountModal>
      )}
    </div>
  );
};

export default Account;
